import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import BrandImage from "../common/BrandImage";
import { NAV_ITEMS } from "../../constants/formOptions";
import { partitionNavPages } from "../../utils/publicPagesApi";

const navButtonClass = (active) =>
  `rounded-md px-3 py-2 text-sm font-medium first-letter:uppercase transition duration-[220ms] [transition-timing-function:var(--smym-ease-out)] active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--smym-accent)] ${
    active
      ? "smym-nav-active"
      : "text-[var(--smym-ink-muted)] hover:text-[var(--smym-ink)]"
  }`;

const Header = ({
  activeSection,
  mobileMenuOpen,
  setMobileMenuOpen,
  scrollToSection,
  navPages = [],
}) => {
  const { inline, overflow } = partitionNavPages(navPages);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const path = window.location.pathname;
  const onHome = path === "/" || path === "";
  const navLabel = (item) => (item === "register" ? "Become a member" : item);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onPointer = (event) => {
      if (!moreRef.current?.contains(event.target)) setMoreOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const handleNavigation = (item) => {
    setMoreOpen(false);
    setMobileMenuOpen(false);
    if (item === "register") {
      window.location.href = "/member-registration";
      return;
    }
    if (!document.getElementById(item)) {
      window.location.href = `/#${item}`;
      return;
    }
    scrollToSection(item);
  };

  const goHome = (event) => {
    event.preventDefault();
    setMobileMenuOpen(false);
    if (!document.getElementById("home")) {
      window.location.href = "/#home";
      return;
    }
    scrollToSection("home");
  };

  const pageHref = (page) => `/p/${encodeURIComponent(page.slug)}`;
  const pageActive = (page) => path === pageHref(page);

  const pageLink = (page, className) => (
    <a
      key={page.slug}
      href={pageHref(page)}
      className={className}
      aria-current={pageActive(page) ? "page" : undefined}
      onClick={() => setMobileMenuOpen(false)}
    >
      {page.navLabel || page.title}
    </a>
  );

  return (
    <header className="fixed top-0 z-50 w-full border-b border-[var(--smym-line)] bg-[color-mix(in_oklch,var(--smym-paper)_94%,transparent)] backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <a
          href="/#home"
          className="flex min-w-0 items-center gap-2.5 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--smym-accent)]"
          onClick={goHome}
        >
          <BrandImage
            src="/smym-logo-no-bg.png?v=20261103"
            alt="SMYM Chennai"
            className="h-9 w-9 shrink-0 object-contain sm:h-10 sm:w-10"
          />
          <span className="truncate text-base font-semibold text-[var(--smym-ink)] sm:text-lg">
            SMYM Chennai
          </span>
        </a>

        <nav className="hidden items-center gap-0.5 md:flex" aria-label="Main">
          {NAV_ITEMS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => handleNavigation(item)}
              className={navButtonClass(onHome && activeSection === item)}
            >
              {navLabel(item)}
            </button>
          ))}
          {inline.map((page) =>
            pageLink(
              page,
              navButtonClass(pageActive(page)).replace(" first-letter:uppercase", ""),
            ),
          )}
          {overflow.length > 0 && (
            <div className="relative" ref={moreRef}>
              <button
                type="button"
                className={navButtonClass(false)}
                aria-expanded={moreOpen}
                aria-haspopup="menu"
                onClick={() => setMoreOpen((open) => !open)}
              >
                More
                <ChevronDown className="ml-1 inline h-3.5 w-3.5" aria-hidden />
              </button>
              {moreOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-20 mt-1 min-w-44 rounded-lg border border-[var(--smym-line)] bg-[var(--smym-paper)] p-1 shadow-[var(--smym-shadow)]"
                >
                  {overflow.map((page) =>
                    pageLink(
                      page,
                      `block rounded-md px-3 py-2 text-sm font-medium ${
                        pageActive(page)
                          ? "smym-nav-active"
                          : "text-[var(--smym-ink)] hover:bg-[var(--smym-accent-soft)]"
                      }`,
                    ),
                  )}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            className="rounded-md p-2 text-[var(--smym-ink)] md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <nav
          className="border-t border-[var(--smym-line)] px-4 py-3 md:hidden"
          aria-label="Mobile"
        >
          <div className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => handleNavigation(item)}
                className={`rounded-md px-3 py-2.5 text-left text-sm font-medium first-letter:uppercase ${
                  onHome && activeSection === item
                    ? "smym-nav-active"
                    : "text-[var(--smym-ink-muted)]"
                }`}
              >
                {navLabel(item)}
              </button>
            ))}
            {inline.map((page) =>
              pageLink(
                page,
                `rounded-md px-3 py-2.5 text-sm font-medium ${
                  pageActive(page) ? "smym-nav-active" : "text-[var(--smym-ink-muted)]"
                }`,
              ),
            )}
            {overflow.length > 0 && (
              <div className="mt-1 border-t border-[var(--smym-line)] pt-2">
                <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--smym-ink-muted)]">
                  More
                </p>
                {overflow.map((page) =>
                  pageLink(
                    page,
                    `block rounded-md px-3 py-2.5 text-sm font-medium ${
                      pageActive(page)
                        ? "smym-nav-active"
                        : "text-[var(--smym-ink-muted)]"
                    }`,
                  ),
                )}
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
